import React, { useCallback, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import cn from 'classnames';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { IconResumeRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconResumeRubric28';
import { IconWrittenResponseRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconWrittenResponseRubric28';
import { IconInterviewRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconInterviewRubric28';
import { IconSearchSvg } from 'apps/sigma2-ui/src/components/svg/IconSearch';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { RubricTagPill } from 'apps/sigma2-ui/src/components/rubrics/RubricTagPill';
import { RubricTagOverflowPill } from 'apps/sigma2-ui/src/components/rubrics/RubricTagOverflowPill';
import { RubricPreviewCategoryContent } from 'apps/sigma2-ui/src/components/rubrics/RubricPreviewCategoryContent';

export interface RubricRoleModalProps {
  roles: RubricRoleModalRowProps[];
  onAdd: (checkedRoles: string[]) => void;
  onDismiss: () => void;
}

export interface RubricRoleModalRowProps {
  name: string;
  liveUses: number;
  useTypes: Record<RubricGradingType, boolean>;
}

const UseTypeBadge = (props: { type: RubricGradingType }) => {
  const language = useLanguage();

  const Icon =
    props.type === 'resume'
      ? IconResumeRubric28Svg
      : props.type === 'questions'
        ? IconWrittenResponseRubric28Svg
        : IconInterviewRubric28Svg;

  return (
    <div
      className={cn('w-[28px] h-[28px] flex items-center justify-center')}
      title={
        props.type === 'resume'
          ? language.resumeRubricShortTitle()
          : props.type === 'questions'
            ? language.writtenResponseRubricMediumTitle()
            : language.interviewRubricShortTitle()
      }
      aria-label={
        props.type === 'resume'
          ? language.resumeRubricShortTitle()
          : props.type === 'questions'
            ? language.writtenResponseRubricMediumTitle()
            : language.interviewRubricShortTitle()
      }
    >
      <Icon
        svg={{
          className: 'w-[28px] h-[28px]',
        }}
      />
    </div>
  );
};

export const RubricRoleModal: React.FC<RubricRoleModalProps> = (props) => {
  const language = useLanguage();

  const [checkedRoles, setCheckedRoles] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return props.roles;
    return props.roles.filter((r) => r.name.toLowerCase().includes(q));
  }, [props.roles, search]);

  const isChecked = useCallback(
    (roleName: string) => checkedRoles.includes(roleName),
    [checkedRoles],
  );

  const toggleRole = useCallback((roleName: string) => {
    setCheckedRoles((prev) => {
      if (prev.includes(roleName)) return prev.filter((x) => x !== roleName);
      return [...prev, roleName];
    });
  }, []);

  const handleClose = useCallback(() => {
    props.onDismiss();
  }, [props]);

  const handleAdd = useCallback(() => {
    if (checkedRoles.length === 0) return;
    props.onAdd(checkedRoles);
    setCheckedRoles([]);
    setSearch('');
  }, [checkedRoles, props]);

  return (
    <Dialog
      open={true}
      as="div"
      className="relative z-overlay"
      onClose={handleClose}
    >
      <div className="fixed inset-0 bg-black/25" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel
            className={cn(
              'w-full max-w-[768px]',
              'rounded-2xl bg-white shadow-12px',
              'overflow-hidden',
            )}
          >
            <div className="px-8 pt-8 pb-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <Dialog.Title as="div">
                    <S2Typography
                      variant="headline-3"
                      textColorVariant="gray-1000"
                      translatedContent={language.rubricRolesModalTitle()}
                    />
                  </Dialog.Title>
                  <div className="mt-1">
                    <S2Typography
                      variant="body-6"
                      textColorVariant="gray-600"
                      translatedContent={language.rubricRolesModalAddRoles()}
                    />
                  </div>
                </div>

                <button
                  onClick={handleClose}
                  className="shrink-0 rounded-lg p-2 hover:bg-gray-50"
                  aria-label={language.cancel()}
                >
                  <IconCloseSvg svg={{ className: 'w-6 h-6 text-gray-600' }} />
                </button>
              </div>

              <div className="mt-6">
                <div className="relative">
                  <IconSearchSvg
                    svg={{
                      className:
                        'absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400',
                    }}
                  />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={language.rubricRolesModalSearchRolesLabel()}
                    className={cn(
                      'w-full h-12 rounded-full',
                      'pl-12 pr-4',
                      'bg-gray-50 border border-gray-200',
                      'text-gray-900 placeholder:text-gray-400',
                      'focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-200',
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="px-8 max-h-[30vh] overflow-y-auto">
              <div className="divide-y divide-gray-100 border-t border-gray-100">
                {filteredRoles.map((role) => {
                  const checked = isChecked(role.name);

                  return (
                    <button
                      key={role.name}
                      type="button"
                      onClick={() => toggleRole(role.name)}
                      className={cn(
                        'w-full py-4 text-left hover:bg-gray-50 rounded-xl transition-colors',
                        // 4 columns: checkbox | name (fills) | live uses (fixed) | icons (fixed)
                        'grid items-center gap-4',
                        'grid-cols-[20px_minmax(0,1fr)_120px_96px]',
                      )}
                    >
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRole(role.name)}
                          onClick={(e) => e.stopPropagation()}
                          className={cn(
                            'h-5 w-5 rounded border-gray-300',
                            'text-indigo-900 focus:ring-indigo-200',
                          )}
                          aria-label={role.name}
                        />
                      </div>

                      <div className="min-w-0">
                        <S2Typography
                          variant="body-6-bold"
                          textColorVariant="gray-1000"
                          translatedContent={role.name}
                          truncate={true}
                        />
                      </div>

                      <div className="justify-self-end whitespace-nowrap">
                        <S2Typography
                          variant="body-6"
                          textColorVariant="gray-600"
                          translatedContent={language.rubricRolesLiveUses({
                            uses: role.liveUses,
                          })}
                        />
                      </div>

                      <div className="justify-self-start flex items-center gap-2">
                        {role.useTypes['resume'] && (
                          <UseTypeBadge type="resume" />
                        )}
                        {role.useTypes['interview'] && (
                          <UseTypeBadge type="interview" />
                        )}
                        {role.useTypes['questions'] && (
                          <UseTypeBadge type="questions" />
                        )}
                      </div>
                    </button>
                  );
                })}

                {filteredRoles.length === 0 && (
                  <div className="py-10 text-center">
                    <S2Typography
                      variant="body-6"
                      textColorVariant="gray-600"
                      translatedContent={language.rubricRolesModalNoMatches()}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="px-8 py-6 flex items-center justify-between">
              <S2Typography
                variant="body-6"
                textColorVariant="gray-600"
                translatedContent={language.rubricRolesSelectedText({
                  count: checkedRoles.length,
                })}
              />

              <PrimaryButtonLightBg
                translatedContent={language.add()}
                disabled={checkedRoles.length === 0}
                onClick={handleAdd}
              />
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export interface RubricCategoryModalProps {
  type: RubricGradingType;
  categories: RubricCategoryModalRowProps[];
  onAdd: (checkedCategoryIds: string[]) => void;
  onDismiss: () => void;
}

export interface RubricCategoryModalRowProps {
  id: string;
  label: string;
  tags: string[];
  criteria: RubricCategoryModalRowCriteriaProps[];
}

export interface RubricCategoryModalRowCriteriaProps {
  criteria: string;
  weight: number;
}

const ArrowCircleButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'w-12 h-12 rounded-full',
        'bg-gray-100 hover:bg-gray-200',
        'flex items-center justify-center',
        'transition-colors',
      )}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="text-indigo-900"
      >
        <path
          d="M8 5L13 10L8 15"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};

const BackButton: React.FC<{ onClick: () => void; label: string }> = ({
  onClick,
  label,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2',
        'rounded-xl px-3 py-2',
        'hover:bg-gray-50 transition-colors',
      )}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="text-indigo-900"
      >
        <path
          d="M12 5L7 10L12 15"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <S2Typography
        variant="body-6-bold"
        textColorVariant="gray-1000"
        translatedContent={label}
      />
    </button>
  );
};

export const RubricCategoryModal: React.FC<RubricCategoryModalProps> = (
  props,
) => {
  const language = useLanguage();

  const libraryTitleMap: Record<RubricGradingType, string> = {
    resume: language.resumeRubricCategoryLibraryTitle(),
    interview: language.interviewRubricCategoryLibraryTitle(),
    questions: language.writtenResponseRubricCategoryLibraryTitle(),
  };

  const searchPlaceholderMap: Record<RubricGradingType, string> = {
    resume: language.resumeRubricCategorySearchPlaceholder(),
    interview: language.interviewRubricCategorySearchPlaceholder(),
    questions: language.writtenResponseRubricCategorySearchPlaceholder(),
  };

  const libraryTitle = libraryTitleMap[props.type];
  const searchPlaceholder = searchPlaceholderMap[props.type];

  const [checkedCategoryIds, setCheckedCategoryIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [previewCategoryId, setPreviewCategoryId] = useState<string | null>(
    null,
  );

  const previewCategory = useMemo(() => {
    if (!previewCategoryId) return null;
    return props.categories.find((c) => c.id === previewCategoryId) ?? null;
  }, [props.categories, previewCategoryId]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return props.categories;
    return props.categories.filter((c) => {
      const inLabel = c.label.toLowerCase().includes(q);
      const inTags = c.tags.some((t) => t.toLowerCase().includes(q));
      return inLabel || inTags;
    });
  }, [props.categories, search]);

  const isChecked = useCallback(
    (id: string) => checkedCategoryIds.includes(id),
    [checkedCategoryIds],
  );

  const toggleCategory = useCallback((id: string) => {
    setCheckedCategoryIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  }, []);

  const handleClose = useCallback(() => {
    props.onDismiss();
  }, [props]);

  const handleAdd = useCallback(() => {
    if (checkedCategoryIds.length === 0) return;
    props.onAdd(checkedCategoryIds);
    setCheckedCategoryIds([]);
    setSearch('');
    setPreviewCategoryId(null);
  }, [checkedCategoryIds, props]);

  return (
    <Dialog
      open={true}
      as="div"
      className="relative z-overlay"
      onClose={handleClose}
    >
      <div className="fixed inset-0 bg-black/25" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel
            className={cn(
              'w-full max-w-[920px]',
              'rounded-2xl bg-white shadow-12px',
              'overflow-hidden',
            )}
          >
            <div className="px-8 pt-8 pb-4">
              <div className="flex items-start justify-between gap-4">
                {previewCategory ? (
                  <div className="flex items-center gap-3">
                    <BackButton
                      onClick={() => setPreviewCategoryId(null)}
                      label={language.back()}
                    />
                  </div>
                ) : (
                  <div className="min-w-0">
                    <Dialog.Title as="div">
                      <S2Typography
                        variant="headline-3"
                        textColorVariant="gray-1000"
                        translatedContent={libraryTitle}
                      />
                    </Dialog.Title>
                    <div className="mt-1">
                      <S2Typography
                        variant="body-6"
                        textColorVariant="gray-600"
                        translatedContent={language.rubricCategoryModalDescription()}
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={handleClose}
                  className="shrink-0 rounded-lg p-2 hover:bg-gray-50"
                  aria-label={language.cancel()}
                >
                  <IconCloseSvg svg={{ className: 'w-6 h-6 text-gray-600' }} />
                </button>
              </div>

              {!previewCategory && (
                <div className="mt-6">
                  <div className="relative">
                    <IconSearchSvg
                      svg={{
                        className:
                          'absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400',
                      }}
                    />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={searchPlaceholder}
                      className={cn(
                        'w-full h-12 rounded-full',
                        'pl-12 pr-4',
                        'bg-gray-50 border border-gray-200',
                        'text-gray-900 placeholder:text-gray-400',
                        'focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-200',
                      )}
                    />
                  </div>
                </div>
              )}
            </div>

            {previewCategory ? (
              <div className="px-8 pb-2">
                <RubricPreviewCategoryContent
                  label={previewCategory.label}
                  tags={previewCategory.tags}
                  criteria={previewCategory.criteria}
                  maxHeightClassName={'max-h-[30vh]'}
                />
              </div>
            ) : (
              <div className="px-8 max-h-[40vh] overflow-y-auto">
                <div className="divide-y divide-gray-100 border-t border-gray-100">
                  {filteredCategories.map((cat) => {
                    const checked = isChecked(cat.id);
                    const tags = cat.tags ?? [];
                    const showTags = tags.slice(0, 2);
                    const extraCount = Math.max(
                      0,
                      tags.length - showTags.length,
                    );

                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => toggleCategory(cat.id)}
                        className={cn(
                          'group w-full py-4 text-left rounded-xl transition-colors',
                          'hover:bg-gray-50',
                          // checkbox | name (fills) | tags (fixed-ish) | criteria (fixed) | arrow (fixed, hover)
                          'grid items-center gap-4',
                          'grid-cols-[20px_minmax(0,1fr)_minmax(240px,360px)_120px_56px]',
                        )}
                      >
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCategory(cat.id)}
                            onClick={(e) => e.stopPropagation()}
                            className={cn(
                              'h-5 w-5 rounded border-gray-300',
                              'text-indigo-900 focus:ring-indigo-200',
                            )}
                            aria-label={cat.label}
                          />
                        </div>

                        <div className="min-w-0">
                          <S2Typography
                            variant="body-6-bold"
                            textColorVariant="gray-1000"
                            translatedContent={cat.label}
                            truncate={true}
                          />
                        </div>

                        <div className="min-w-0 flex items-center gap-2 overflow-hidden">
                          {showTags.map((t) => (
                            <RubricTagPill key={t} name={t} />
                          ))}
                          {extraCount > 0 && (
                            <RubricTagOverflowPill
                              overflowCount={extraCount}
                              tags={tags}
                            />
                          )}
                        </div>

                        <div className="justify-self-end whitespace-nowrap">
                          <S2Typography
                            variant="body-6"
                            textColorVariant="gray-600"
                            translatedContent={language.rubricCategoryModalCriteriaCount(
                              {
                                count: cat.criteria.length,
                              },
                            )}
                          />
                        </div>

                        <div className="justify-self-end">
                          <div
                            className={cn(
                              'opacity-0 group-hover:opacity-100',
                              'transition-opacity',
                            )}
                          >
                            <ArrowCircleButton
                              onClick={() => setPreviewCategoryId(cat.id)}
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {filteredCategories.length === 0 && (
                    <div className="py-10 text-center">
                      <S2Typography
                        variant="body-6"
                        textColorVariant="gray-600"
                        translatedContent={language.rubricCategoryModalNoMatches()}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="px-8 py-6 flex items-center justify-between">
              <S2Typography
                variant="body-6"
                textColorVariant="gray-600"
                translatedContent={language.rubricRolesSelectedText({
                  count: checkedCategoryIds.length,
                })}
              />

              <PrimaryButtonLightBg
                translatedContent={language.add()}
                disabled={checkedCategoryIds.length === 0}
                onClick={handleAdd}
              />
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};
